import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("shreya.jhanwar@analyst.work");
  const [password, setPassword] = useState("Admin@123analyst");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (user && typeof user === "object") return <Navigate to="/app" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name.split(" ")[0]}.`);
      navigate("/app", { replace: true });
    } else {
      setError(res.error || "Login failed");
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      {/* Left pane */}
      <div
        className="relative hidden md:block"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(28,28,25,0.35) 0%, rgba(28,28,25,0.75) 100%), url('https://images.unsplash.com/photo-1529160638848-c6c71fee1cb7?q=80&w=1600')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="flex h-full flex-col justify-between p-12 text-[#f7f7f5]">
          <div className="overline text-[#d9d9d1]">Shreya · Data Analyst · Workspace</div>
          <div>
            <h1 className="max-w-md font-display text-4xl font-black leading-[1.1] tracking-tighter">
              Data, decisions, and calm operations — all in one place.
            </h1>
            <p className="mt-4 max-w-md text-sm text-[#d9d9d1]">
              A personal workspace for tracking my team, tasks, leaves and
              analytics dashboards.
            </p>
          </div>
        </div>
      </div>

      {/* Right pane */}
      <div className="flex items-center justify-center bg-[#f7f7f5] px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="overline">Employee workspace</div>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">
            Sign in
          </h2>
          <p className="mt-2 text-sm text-[#575752]">
            Use your workspace credentials to continue.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                autoComplete="email"
                required
                className="focus-visible:ring-[#4a6b53]/30"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-medium">
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                className="focus-visible:ring-[#4a6b53]/30"
              />
            </div>

            {error ? (
              <div
                data-testid="login-error"
                className="rounded-md border border-[#f1c8c8] bg-[#fcecec] px-3 py-2 text-sm text-[#8f2c2c]"
              >
                {error}
              </div>
            ) : null}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={submitting}
              className="w-full bg-[#4a6b53] text-white hover:bg-[#3d5a45] transition-colors duration-200 ease-in-out"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in…
                </>
              ) : (
                "Sign in securely"
              )}
            </Button>
          </form>

          <div className="mt-8 rounded-md border border-[#e5e5e0] bg-white p-4 text-xs text-[#575752]">
            <div className="overline mb-2">Demo credentials</div>
            <div className="font-mono">
              shreya.jhanwar@analyst.work · Admin@123analyst
              <br />
              priya.nair@analyst.work · Employee@123
            </div>
          </div>

          <p className="mt-6 text-[11px] leading-relaxed text-[#8a8a80]">
            Access is restricted to authorized users only. All activity may be
            logged for security and compliance purposes.
          </p>
        </div>
      </div>
    </div>
  );
}
